import styles from './headerMenu.module.scss'
import { useTranslation } from "react-i18next";
import {
  Dropdown,
  DropdownItem
} from "../../../shared/components/dropdown/dropdown";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../../store";
import {
  setSelectedEnvironment,
  setSelectedVariables,
  setTestrailEnable
} from "../../../store/slices/mainSlice";
import {
  useForm,
  useWatch
} from "react-hook-form";
import {
  number,
  object
} from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { SwitchCheckbox } from "../../../shared/components/switchCheckbox/switchCheckbox";
import { TabsConstants } from "../../../constants/tabs.constants";
import { Environment } from "../../../types/environmentTypes";
import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import editIcon from "../../../shared/images/edit.svg";
import addBlockIcon from "../../../shared/images/addBlock.svg";
import deleteIcon from "../../../shared/images/delete.svg";
import { MessageWindow } from "../../../shared/components/messageWindow/messageWindow";
import { EnvironmentModal } from "../../environmentModal/environmentModal";
import {
  useDeleteEnvironmentMutation,
  useGetAllEnvironmentsQuery
} from "../../../api/environmentService";
import variablesManagerIcon from "../../../shared/images/variablesManager.svg";
import { VariablesManager } from "../../variablesManager/variablesManager";
import { useGetAllVariablesQuery } from "../../../api/variableService";
import { SelectedVariable } from "../../../types/variableType";
import { findProjectSettings } from "../../../store/selectors";

export const HeaderMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const testrailEnable = useSelector((state: RootState) => findProjectSettings(state).testrailEnable);
  const selectedTab = useSelector((state: RootState) => findProjectSettings(state).selectedTab);
  const selectedEnvironment = useSelector((state: RootState) => findProjectSettings(state).selectedEnvironment);
  const isSandbox = useSelector((state: RootState) => state.authSlice.isSandbox);

  const [localTestrailEnable, setLocalTestrailEnable] = useState(testrailEnable);
  const [showVariablesManagerModal, setShowVariablesManagerModal] = useState<boolean>(false);

  const [showEnvironmentModal, setShowEnvironmentModal] = useState<boolean>(false);
  const [editEnvironment, setEditEnvironment] = useState<Environment>()
  const [isOpenEnvironmentDropdown, setIsOpenEnvironmentDropdown] = useState<boolean>(false);
  const [showDeleteEnvironmentModal, setShowDeleteEnvironmentModal] = useState<boolean>(false);

  const { data: environments, refetch: refetchEnvironments } = useGetAllEnvironmentsQuery("");
  const [deleteEnvironment, { isSuccess: isSuccessDeleteEnvironment }] = useDeleteEnvironmentMutation();

  const { data: variables } = useGetAllVariablesQuery("");

  useEffect(() => {
    if (selectedTab === TabsConstants.testCaseIncoming) {
      setLocalTestrailEnable(false);
    } else {
      setLocalTestrailEnable(testrailEnable);
    }
  }, [testrailEnable, selectedTab, setLocalTestrailEnable]);

  useEffect(() => {
    if (!!environments) {
      const findEnv = environments.find(item => item.id === selectedEnvironment.id);
      if (!!findEnv) {
        dispatch(setSelectedEnvironment(findEnv));
      } else if (!!environments[0]) {
        dispatch(setSelectedEnvironment(environments[0]));
      } else {
        dispatch(setSelectedEnvironment({
          id: 0,
          servers: [],
          testRunId: 0,
          name: "",
          defaultVariableValues: [],
          defaultHeaders: []
        }));
      }
    }
  }, [environments, dispatch]);

  useEffect(() => {
    if (!!variables) {
      const newSelectedVariables: Array<SelectedVariable> = variables.map(variable => {
        const defaultVariable = selectedEnvironment.defaultVariableValues?.find(item => item.variableId === variable.id);
        if (!!defaultVariable) {
          const variableValue = variable.values.find(item => item.id === defaultVariable.variableValueId);
          if (variableValue) {
            return {
              id: variable.id,
              name: variable.name,
              value: variableValue
            }
          }
        }
        return {
          id: variable.id,
          name: variable.name,
          value: {
            id: variable.values[0].id,
            value: variable.values[0].value
          }
        }
      });
      dispatch(setSelectedVariables(newSelectedVariables));
    }
  }, [selectedEnvironment, variables, dispatch]);

  useEffect(() => {
    if (isSuccessDeleteEnvironment) {
      setEditEnvironment(undefined);
      refetchEnvironments();
    }
  }, [isSuccessDeleteEnvironment, refetchEnvironments, setEditEnvironment]);

  const schema = object({
    environment: number().required()
  });

  const { control } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      environment: selectedEnvironment.id || 0
    }
  });
  const environmentWatch = useWatch({ control, name: "environment" });

  useEffect(() => {
    const findEnvironment = environments?.find(environment => environment.id === environmentWatch);
    if (!!findEnvironment) {
      dispatch(setSelectedEnvironment(findEnvironment));
    }
  }, [environmentWatch, environments, dispatch, setEditEnvironment]);

  const clickEditEnvironment = useCallback((e: MouseEvent<HTMLDivElement>, environment: Environment): void => {
    e.stopPropagation();
    setEditEnvironment(environment);
    setShowEnvironmentModal(true);
    setIsOpenEnvironmentDropdown(false);
  }, [setEditEnvironment, setShowEnvironmentModal, setIsOpenEnvironmentDropdown])

  const clickDeleteEnvironment = useCallback((e: MouseEvent<HTMLDivElement>, environment: Environment): void => {
    e.stopPropagation();
    setEditEnvironment(environment);
    setShowDeleteEnvironmentModal(true);
    setIsOpenEnvironmentDropdown(false);
  }, [setEditEnvironment, setShowDeleteEnvironmentModal, setIsOpenEnvironmentDropdown]);

  const onChangeTestrailCheckbox = useCallback((value: boolean): void => {
    dispatch(setTestrailEnable(value));
  }, [dispatch])

  const closeEnvironmentModal = useCallback((isSave: boolean) => {
    setShowEnvironmentModal(false);
    if (isSave) {
      refetchEnvironments();
    }
    setEditEnvironment(undefined);
  }, [setShowEnvironmentModal, dispatch, refetchEnvironments, setEditEnvironment, editEnvironment, selectedEnvironment]);

  const clickAddEnvironment = useCallback((): void => {
    setShowEnvironmentModal(true);
  }, [setShowEnvironmentModal]);

  const closeDeleteEnvironmentModal = useCallback((isConfirm: boolean): void => {
    if (isConfirm && !!editEnvironment?.id) {
      deleteEnvironment(editEnvironment.id);
    }
    setShowDeleteEnvironmentModal(false);
  }, [deleteEnvironment, editEnvironment, setShowDeleteEnvironmentModal])

  const showDeleteEnvironment: boolean = useMemo(() => {
    return environments?.length !== 1;
  }, [environments]);

  const getEnvironmentDropdownItems: DropdownItem[] = useMemo(() => {
    if (!!environments) {
      return environments.map(environment => {
        return {
          id: environment.id,
          name: (
            <div className={styles.option}>
              <div className={styles.info}>
                <span title={environment.name}>{environment.name}</span>
              </div>
              <div className={styles.actionsContainer}>
                { showDeleteEnvironment && (
                  <div className={styles.item} onClick={(e) => clickDeleteEnvironment(e, environment)}>
                    <img src={deleteIcon} alt=""/>
                  </div>
                )}
                <div className={styles.item} onClick={(e) => clickEditEnvironment(e, environment)}>
                  <img src={editIcon} alt=""/>
                </div>
              </div>
            </div>
          ),
          title: environment.name
        }
      })
    } else {
      return [{
        id: selectedEnvironment.id,
        name: t("common.loading"),
        title:  t("common.loading")
      }];
    }
  }, [environments]);

  const disabled = useMemo(() => {
    return selectedTab === TabsConstants.testCaseIncoming;
  }, [selectedTab]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.environment}>
          <div className={styles.item}>
            <div className={styles.label}>{t("common.environment")}</div>
            <div className={styles.dropdown}>
              <Dropdown control={control}
                        disabled={disabled}
                        fontPopupClass={styles.fontDropdownPopup}
                        selectedItemClass={styles.selectedItemDropdown}
                        items={getEnvironmentDropdownItems}
                        name={"environment"}
                        isOpenProp={isOpenEnvironmentDropdown}
                        setIsOpenProp={setIsOpenEnvironmentDropdown}/>
            </div>
            <div className={styles.addButton} onClick={clickAddEnvironment}>
              <img src={addBlockIcon} alt=""/>
            </div>
          </div>
          <div className={`${styles.variablesManager} ${disabled ? styles.disabled : ""}`}
               onClick={() => !disabled && setShowVariablesManagerModal(true)}>
            <img src={variablesManagerIcon} alt=""/>
            {t("common.variablesManager")}
          </div>
        </div>
        <div className={styles.testrail}>
          {!isSandbox && (
            <div className={styles.item}>
              <div className={styles.label}>{t("common.testRail")}</div>
              <SwitchCheckbox isChecked={localTestrailEnable}
                              disabled={selectedTab === TabsConstants.testCaseIncoming}
                              onChange={onChangeTestrailCheckbox}/>
            </div>
          )}

        </div>
      </div>

      {showVariablesManagerModal && (<VariablesManager onClose={() => setShowVariablesManagerModal(false)}/>) }

      {showEnvironmentModal && (<EnvironmentModal environment={editEnvironment}
                                                  showDelete={showDeleteEnvironment}
                                                  onClose={closeEnvironmentModal}/>
      )}

      {showDeleteEnvironmentModal && !!editEnvironment && (
        <MessageWindow onClose={closeDeleteEnvironmentModal}
                       title={t("environment.delete")}
                       description={<>
                         {t("environment.deleteDescription")} <strong>{editEnvironment.name}</strong>?
                       </>}
                       primaryText={t("common.ok")}
                       secondaryText={t("common.cancel")}/>
      )}
    </>
  )
}